import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  DollarSign,
  Calendar,
  Users,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { obtenerPlanes, eliminarPlan, activarPlan, desactivarPlan } from '../../services/centroService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Funciones de notificación estandarizadas
const showSuccessToast = (message) => {
    toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        },
        containerId: 'planes'
    });
};

const showErrorToast = (message) => {
    toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        },
        containerId: 'planes'
    });
};

const showWarningToast = (message) => {
    toast.warning(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        },
        containerId: 'planes'
    });
};

const GestionPlanes = () => {
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [planToActivate, setPlanToActivate] = useState(null);
  const [planToDeactivate, setPlanToDeactivate] = useState(null);

  useEffect(() => {
    cargarPlanes();
  }, []);

  const cargarPlanes = async () => {
    try {
      setLoading(true);
      // Obtener todos los planes incluyendo inactivos para gestión completa
      const response = await obtenerPlanes();
      
      if (response && response.success) {
        setPlanes(response.data || []);
      } else if (response && Array.isArray(response)) {
        // Si la respuesta es directamente un array
        setPlanes(response);
      } else {
        console.error('Estructura de respuesta inesperada:', response);
        showErrorToast('Error al cargar los planes: estructura de datos incorrecta');
        setPlanes([]);
      }
    } catch (error) {
      console.error('Error al cargar planes:', error);
      showErrorToast('Error al cargar los planes');
      setPlanes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEliminarPlan = async (id) => {
    try {
      // Verificar si es el último plan activo
      const planesActivos = planes.filter(p => p.estado === true);
      if (planesActivos.length === 1 && planesActivos[0].id_plan === id) {
        showErrorToast('No se puede eliminar el último plan activo. Debe haber al menos un plan activo para las matrículas.');
        setShowDeleteModal(false);
        setPlanToDelete(null);
        return;
      }

      const response = await eliminarPlan(id);
      if (response.success) {
        showSuccessToast('Plan eliminado correctamente');
        cargarPlanes();
      } else {
        showErrorToast(response.message || 'Error al eliminar el plan');
      }
    } catch (error) {
      console.error('Error al eliminar plan:', error);
      showErrorToast('Error al eliminar el plan');
    }
    setShowDeleteModal(false);
    setPlanToDelete(null);
  };

  const confirmarEliminacion = (plan) => {
    // Verificar si es el último plan activo
    const planesActivos = planes.filter(p => p.estado === true);
    if (planesActivos.length === 1 && plan.estado === true) {
      showErrorToast('No se puede eliminar el último plan activo. Debe haber al menos un plan activo para las matrículas.');
      return;
    }
    setPlanToDelete(plan);
    setShowDeleteModal(true);
  };

  const confirmarActivacion = (plan) => {
    setPlanToActivate(plan);
    setShowActivateModal(true);
  };

  const handleActivarPlan = async (id) => {
    try {
      const response = await activarPlan(id);
      if (response.success) {
        showSuccessToast('Plan activado correctamente');
        cargarPlanes();
      } else {
        showErrorToast(response.message || 'Error al activar el plan');
      }
    } catch (error) {
      console.error('Error al activar plan:', error);
      showErrorToast('Error al activar el plan');
    }
    setShowActivateModal(false);
    setPlanToActivate(null);
  };

  const confirmarDesactivacion = (plan) => {
    // Verificar si es el último plan activo
    const planesActivos = planes.filter(p => p.estado === true);
    if (planesActivos.length === 1 && plan.estado === true) {
      showErrorToast('No se puede desactivar el último plan activo. Debe haber al menos un plan activo para las matrículas.');
      return;
    }
    setPlanToDeactivate(plan);
    setShowDeactivateModal(true);
  };

  const handleDesactivarPlan = async (id) => {
    try {
      // Verificar si es el último plan activo
      const planesActivos = planes.filter(p => p.estado === true);
      if (planesActivos.length === 1 && planesActivos[0].id_plan === id) {
        showErrorToast('No se puede desactivar el último plan activo. Debe haber al menos un plan activo para las matrículas.');
        setShowDeactivateModal(false);
        setPlanToDeactivate(null);
        return;
      }

      const response = await desactivarPlan(id);
      if (response.success) {
        showSuccessToast('Plan desactivado correctamente');
        cargarPlanes();
      } else {
        showErrorToast(response.message || 'Error al desactivar el plan');
      }
    } catch (error) {
      console.error('Error al desactivar plan:', error);
      showErrorToast('Error al desactivar el plan');
    }
    setShowDeactivateModal(false);
    setPlanToDeactivate(null);
  };

  const planesFiltrados = planes.filter(plan =>
    plan.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plan.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatearPrecio = (precio) => {
    // Verificar si el precio es válido
    const precioNumerico = parseFloat(precio);
    if (isNaN(precioNumerico) || precioNumerico === null || precioNumerico === undefined) {
      return '$0.00';
    }
    return new Intl.NumberFormat('es-EC', {
      style: 'currency',
      currency: 'USD'
    }).format(precioNumerico);
  };

  const getEstadoColor = (estado) => {
    if (estado === true) {
      return 'bg-green-100 text-green-800';
    } else if (estado === false) {
      return 'bg-red-100 text-red-800';
    } else {
      return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando planes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gestión de Planes</h1>
                <p className="text-gray-600">Administra los planes del centro infantil</p>
              </div>
            </div>
            <Link
              to="/admin/centro/planes/crear"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Plus className="h-5 w-5" />
              Nuevo Plan
            </Link>
          </div>
        </div>

        {/* Barra de búsqueda */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Buscar planes por nombre o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors shadow-sm"
            />
          </div>
        </div>

        {/* Lista de planes */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {planesFiltrados.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm ? 'No se encontraron planes' : 'No hay planes registrados'}
              </h3>
              <p className="text-gray-600 mb-4">
                {searchTerm 
                  ? 'Intenta con otros términos de búsqueda'
                  : 'Comienza creando tu primer plan'
                }
              </p>
              {!searchTerm && (
                <Link
                  to="/admin/centro/planes/crear"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <Plus className="h-5 w-5" />
                  Crear Plan
                </Link>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wide">Plan</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wide">Descripción</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wide">Precio</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wide">Horario</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wide">Estado</th>
                    <th className="text-center py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wide">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {planesFiltrados.map((plan) => (
                    <tr key={plan.id_plan} className="hover:bg-blue-50 transition-colors duration-200">
                      <td className="py-5 px-6">
                        <div>
                          <h3 className="font-semibold text-gray-900 text-base">{plan.nombre}</h3>
                        </div>
                      </td>
                      <td className="py-5 px-6">
                        <p className="text-sm text-gray-600 line-clamp-2">{plan.descripcion}</p>
                      </td>
                      <td className="py-5 px-6">
                        <div className="flex items-center gap-2">
                          <div className="p-1 bg-green-100 rounded-full">
                            <DollarSign className="h-4 w-4 text-green-600" />
                          </div>
                          <span className="font-semibold text-gray-900 text-base">
                            {formatearPrecio(plan.monto)}
                          </span>
                        </div>
                      </td>
                      <td className="py-5 px-6">
                        <div className="flex items-center gap-2">
                          <div className="p-1 bg-blue-100 rounded-full">
                            <Calendar className="h-4 w-4 text-blue-600" />
                          </div>
                          <span className="text-gray-900 font-medium">
                            {plan.hora_inicio} - {plan.hora_fin}
                          </span>
                        </div>
                      </td>
                      <td className="py-5 px-6">
                        <div className="flex items-center gap-3">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                            getEstadoColor(plan.estado)
                          }`}>
                            {plan.estado === true ? (
                              <CheckCircle className="h-4 w-4 mr-1.5" />
                            ) : (
                              <AlertCircle className="h-4 w-4 mr-1.5" />
                            )}
                            {plan.estado === true ? 'Activo' : plan.estado === false ? 'Inactivo' : 'Sin estado'}
                          </span>
                          
                          {/* Botones de activar/desactivar en la columna Estado */}
                          {plan.estado === true ? (
                            <button
                              onClick={() => confirmarDesactivacion(plan)}
                              className={`p-1.5 rounded-lg transition-all duration-200 ${
                                planes.filter(p => p.estado === true).length === 1
                                  ? 'text-gray-400 cursor-not-allowed'
                                  : 'text-orange-600 hover:bg-orange-100 hover:scale-105'
                              }`}
                              title={planes.filter(p => p.estado === true).length === 1
                                ? 'No se puede desactivar el último plan activo'
                                : 'Desactivar plan'
                              }
                              disabled={planes.filter(p => p.estado === true).length === 1}
                            >
                              <AlertCircle className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => confirmarActivacion(plan)}
                              className="p-1.5 text-green-600 hover:bg-green-100 rounded-lg transition-all duration-200 hover:scale-105"
                              title="Activar plan"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="py-5 px-6">
                        <div className="flex items-center justify-center gap-2">
                          <Link
                            to={`/admin/centro/planes/editar/${plan.id_plan}`}
                            className="p-2.5 text-blue-600 hover:bg-blue-100 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Editar plan"
                          >
                            <Edit className="h-5 w-5" />
                          </Link>
                          <button
                            onClick={() => confirmarEliminacion(plan)}
                            className={`p-2.5 rounded-lg transition-all duration-200 hover:scale-105 ${
                              planes.filter(p => p.estado === true).length === 1 && plan.estado === true
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-red-600 hover:bg-red-100'
                            }`}
                            title={planes.filter(p => p.estado === true).length === 1 && plan.estado === true
                              ? 'No se puede eliminar el último plan activo'
                              : 'Eliminar plan'
                            }
                            disabled={planes.filter(p => p.estado === true).length === 1 && plan.estado === true}
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal de confirmación de eliminación */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4 transform transition-all duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Confirmar eliminación</h3>
              </div>
              <p className="text-gray-600 mb-8 leading-relaxed">
                ¿Estás seguro de que deseas eliminar el plan <strong className="text-gray-900">{planToDelete?.nombre}</strong>? 
                Esta acción no se puede deshacer y se perderán todos los datos asociados.
              </p>
              <div className="flex gap-4 justify-end">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setPlanToDelete(null);
                  }}
                  className="px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200 font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleEliminarPlan(planToDelete.id_plan)}
                  className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 font-medium"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de confirmación de activación */}
        {showActivateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4 transform transition-all duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Confirmar activación</h3>
              </div>
              <p className="text-gray-600 mb-8 leading-relaxed">
                ¿Estás seguro de que deseas activar el plan <strong className="text-gray-900">{planToActivate?.nombre}</strong>? 
                Este plan estará disponible para nuevas matrículas.
              </p>
              <div className="flex gap-4 justify-end">
                <button
                  onClick={() => {
                    setShowActivateModal(false);
                    setPlanToActivate(null);
                  }}
                  className="px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200 font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleActivarPlan(planToActivate.id_plan)}
                  className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 font-medium"
                >
                  Activar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de confirmación de desactivación */}
        {showDeactivateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4 transform transition-all duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-orange-100 rounded-full">
                  <AlertCircle className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Confirmar desactivación</h3>
              </div>
              <p className="text-gray-600 mb-8 leading-relaxed">
                ¿Estás seguro de que deseas desactivar el plan <strong className="text-gray-900">{planToDeactivate?.nombre}</strong>? 
                Este plan no estará disponible para nuevas matrículas, pero las matrículas existentes no se verán afectadas.
              </p>
              <div className="flex gap-4 justify-end">
                <button
                  onClick={() => {
                    setShowDeactivateModal(false);
                    setPlanToDeactivate(null);
                  }}
                  className="px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200 font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleDesactivarPlan(planToDeactivate.id_plan)}
                  className="px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg hover:from-orange-700 hover:to-orange-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 font-medium"
                >
                  Desactivar
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Toast Container */}
        <ToastContainer 
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          className="mt-16"
          limit={3}
          enableMultiContainer
          containerId="planes"
          style={{ zIndex: 9999 }}
        />
      </div>
    </div>
  );
};

export default GestionPlanes;